/**
 * RequireRole — declarative role gate for authenticated routes.
 *
 * Renders a translated 403 page when the current user's role isn't in the allowed set.
 * The backend already 403s the underlying API call; this surface gives the UI a
 * graceful page-level error instead of a sea of toasts. Defense in depth.
 */
import type { ReactNode } from "react";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "@/lib/auth";
import { TOKENS } from "@/lib/tokens";
import type { Role } from "@/types/api";

export function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children?: ReactNode;
}) {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation("errors");

  if (!user || !roles.includes(user.role)) {
    return (
      <div
        className="p-8 text-center"
        role="alert"
        style={{ color: TOKENS.text }}
      >
        <h1 className="text-2xl font-semibold mb-2">403</h1>
        <p style={{ color: TOKENS.textMuted }}>{t("forbidden")}</p>
      </div>
    );
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}
